import { omit } from 'lodash';
import { useCallback } from 'react';

import { useManagedAssetChooser } from 'components/choosers/AssetChooser/useManagedAssetChooser';
import {
  bladeAtlas,
  bladeSide,
  isConfirmed,
  numberAndConfirmed,
  siteAtlas,
  turbineAtlas,
} from 'components/damages/static';
import { DamageTable } from 'components/DamageTable';
import { DAMAGE_TABLE_STORAGE_KEY } from 'components/DataTable/Views/helpers/tableStorageKeys';
import { Button } from 'components/ui';
import damageListQuery from 'queries/DamageList.atlas.gql';
import { type AtlasGqlDamage, type AtlasGqlHorizonDamage } from 'types/atlas-graphql';
import { TABLE_SCROLL_WIDTH_SMALL } from 'utils/constants';
import { useFeatures } from 'utils/features';
import { SelectableAction, useSelectableRecords } from 'utils/selectable';

import { generateDamageTableFilters } from '../../../horizon/routes/Inspections/DamageList/utils';
import { type TQueryFilter } from '../../DataTable/types';
import { AssetChooser } from '../index';

import { DamageChooser2 } from './DamageChooser2';

const columns = [numberAndConfirmed, isConfirmed, siteAtlas, turbineAtlas, bladeAtlas, bladeSide];

type IDamageChooserProps = {
  onSubmit: Function;
  tableProps?: any;
};

export const DamageChooser: React.FunctionComponent<IDamageChooserProps> = ({
  onSubmit,
  ...tableProps
}) => {
  const { HORIZON_DAMAGES, LEGACY_DAMAGES } = useFeatures().features;

  const { selected, rowSelection, onClearSelection } = useSelectableRecords();
  const { onChange: handleAssetChooserChange, locationIds, turbineIds } = useManagedAssetChooser();

  const universalFilters: TQueryFilter[] = generateDamageTableFilters({
    siteIds: locationIds,
    turbineIds,
  });

  function transformDamages(input: any) {
    return input.damages || [];
  }

  const handleAdd = useCallback(
    (values: any) => {
      onSubmit(
        values.map((item: AtlasGqlDamage | AtlasGqlHorizonDamage) => {
          const noKeyItem = omit(item, 'key');
          const assetId = item?.asset?.id || item.assetId;
          const damageBranchId = 'branchGlobalId' in item ? item?.branchGlobalId : '';
          const horizonDamageId = item?.__typename === 'HorizonDamage' ? item.id : '';

          if (assetId && (damageBranchId || horizonDamageId)) {
            return { ...noKeyItem, assetId, damageBranchId, horizonDamageId };
          }
          return noKeyItem;
        })
      );
    },
    [onSubmit, selected]
  );

  const addAction = (
    <SelectableAction selected={selected} onClear={onClearSelection}>
      <Button
        type="primary"
        id="damage-chooser-add-damage-button"
        disabled={!selected.length}
        onClick={() => handleAdd(selected)}
      >
        Add
      </Button>
    </SelectableAction>
  );

  if (HORIZON_DAMAGES) {
    return <DamageChooser2 controls={true} handleAdd={handleAdd} />;
  } else if (LEGACY_DAMAGES) {
    return (
      <DamageTable
        schema={undefined}
        id="damage-chooser-table"
        scrollWidth={TABLE_SCROLL_WIDTH_SMALL}
        size="small"
        columns={columns}
        storageKey={DAMAGE_TABLE_STORAGE_KEY}
        showColumnPicker={true}
        dataQuery={damageListQuery}
        transform={transformDamages}
        routeProps={{
          universalFilter: universalFilters,
        }}
        rowSelection={rowSelection}
        controls={
          <AssetChooser
            width={'50%'}
            onChange={handleAssetChooserChange}
            bladeIds={[]}
            turbineIds={turbineIds}
            locationIds={locationIds}
            callerManagedState={true}
            settings={{ blade: { visible: false } }}
            small={false}
            block={false}
          />
        }
        actions={addAction}
        defaultActiveTabKey="SEVERE"
        {...tableProps}
      />
    );
  }

  return null;
};
